import { useState } from 'react';
import { Modal, ModalContent, Button, Input, Control, Label, EditIcon } from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { sftpUsersApi } from '@/api/sftpUsers';
import type { SftpUser, UpdateSftpUserPayload } from '@/api/sftpUsers';

interface EditSftpUserDialogProps {
  sftpUser: SftpUser;
  onClose: () => void;
  onSuccess: () => void;
}

// IPv4 CIDR, e.g. 10.0.0.0/24 or a bare address 203.0.113.5. Kept intentionally
// permissive — the backend is the source of truth, this only catches typos.
const looksLikeCidr = (value: string): boolean =>
  /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(value.trim());

const EditSftpUserDialog = ({ sftpUser, onClose, onSuccess }: EditSftpUserDialogProps) => {
  const [homeDirectory, setHomeDirectory] = useState(sftpUser.homeDirectory ?? '');
  const [allowedSourceCidr, setAllowedSourceCidr] = useState(sftpUser.allowedSourceCidr ?? '');

  const { mutate: update, isSubmitting } = useApiMutation<UpdateSftpUserPayload, SftpUser>({
    mutationFn: (data) => sftpUsersApi.update(sftpUser.id, data),
    successMessage: 'SFTP credential updated',
    errorMessage: 'Failed to update SFTP credential',
    onSuccess: () => onSuccess(),
  });

  const cidrValue = allowedSourceCidr.trim();
  const cidrValid = cidrValue === '' || looksLikeCidr(cidrValue);
  const isValid = cidrValid;

  const handleSubmit = async () => {
    if (!isValid) return;
    const home = homeDirectory.trim();
    await update({
      homeDirectory: home === '' ? null : home,
      allowedSourceCidr: cidrValue === '' ? null : cidrValue,
    });
  };

  return (
    <Modal
      title="Edit SFTP Credential"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<EditIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-color-secondary">
            Update the home directory and source IP restriction for{' '}
            <span className="font-bold">{sftpUser.username}</span>.
          </p>

          <Control hideSpacing="bottom">
            <Label htmlFor="edit-home">Home Directory</Label>
            <Input
              id="edit-home"
              placeholder="e.g., /jcpl"
              value={homeDirectory}
              onChange={(e) => setHomeDirectory(e.target.value)}
            />
            <p className="mt-1 text-xs text-color-tertiary">
              Leave blank to use the default home directory.
            </p>
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="edit-cidr">Allowed Source CIDR</Label>
            <Input
              id="edit-cidr"
              placeholder="e.g., 203.0.113.0/24"
              value={allowedSourceCidr}
              onChange={(e) => setAllowedSourceCidr(e.target.value)}
            />
            {cidrValue !== '' && !cidrValid ? (
              <p className="mt-1 text-xs text-color-error">
                Enter a valid IPv4 address or CIDR range (e.g. 203.0.113.0/24).
              </p>
            ) : (
              <p className="mt-1 text-xs text-color-tertiary">
                Leave blank to allow connections from any source IP.
              </p>
            )}
          </Control>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default EditSftpUserDialog;
